import './SupportChatPreview.scss';
import { CSSProperties } from 'react';
import clsx from 'clsx';
import { CloseButton } from '@mantine/core';
import { MdMic } from 'react-icons/md';
import { getInitials } from '@srm/ui/avatar';
import Button from '@srm/ui/button';

/** Где всплывающее окно относительно кнопки «Чат»: над ней, если над полосой есть место, иначе под ней. */
export type SupportPopupPlacement = 'above' | 'below';

export interface ISupportChatPreviewProps {
    /** Автор сообщения или специалист, включивший микрофон */
    author: string;
    /** Цвет участника — кружок с инициалами */
    color?: string;
    /** Текст сообщения (до двух строк) или пояснение */
    text: string;
    /** `message` — новое сообщение в свёрнутом чате, `mic` — специалист включил микрофон */
    kind?: 'message' | 'mic';
    /** Щелчок по превью — открыть чат */
    onOpen?: () => void;
    /** Кнопка под текстом («Включить микрофон») */
    actionLabel?: string;
    onAction?: () => void;
    onClose: () => void;
    placement?: SupportPopupPlacement;
    className?: string;
    style?: CSSProperties;
}

/**
 * Превью у кнопки «Чат» полосы показа (IMPLEMENTATION.md §30): новое сообщение специалиста, пока чат свёрнут, или
 * «специалист включил микрофон» с кнопкой «Включить микрофон». Рисуется в слое оверлеев поддержки — вне захвата и вне
 * удалённого управления (`data-remote-control-exclude`).
 */
const SupportChatPreview = ({
    author,
    color,
    text,
    kind = 'message',
    onOpen,
    actionLabel,
    onAction,
    onClose,
    placement = 'below',
    className,
    style,
}: ISupportChatPreviewProps) => {
    const avatar = (
        <span
            className="support-chat-preview__avatar"
            aria-hidden
            style={{ '--support-chat-preview-color': color ?? 'var(--neutral-50)' } as CSSProperties}
        >
            {getInitials(author)}
            {kind === 'mic' && (
                <span className="support-chat-preview__mic">
                    <MdMic size={10} />
                </span>
            )}
        </span>
    );
    const body = (
        <>
            {avatar}
            <span className="support-chat-preview__content">
                <span className="support-chat-preview__author">{author}</span>
                <span className="support-chat-preview__text">{text}</span>
            </span>
        </>
    );
    return (
        <div
            className={clsx('support-chat-preview', className)}
            data-placement={placement}
            data-kind={kind}
            data-remote-control-exclude
            data-testid="support-chat-preview"
            style={style}
        >
            <div className="support-chat-preview__row">
                {onOpen ? (
                    <button
                        type="button"
                        className="support-chat-preview__main"
                        aria-label={`Открыть чат. ${author}: ${text}`}
                        onClick={onOpen}
                    >
                        {body}
                    </button>
                ) : (
                    <div className="support-chat-preview__main">{body}</div>
                )}
                <CloseButton size="sm" aria-label="Скрыть" title="Скрыть" onClick={onClose} />
            </div>
            {actionLabel && onAction && (
                <div className="support-chat-preview__actions">
                    <Button size="sm" startAdornment={<MdMic size={18} />} onClick={onAction}>
                        {actionLabel}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SupportChatPreview;
